import { FC, useRef } from 'react'
import styles from './WallOfLove.module.scss'
import { TweetItem } from './wallOfLoveIterfaces'
import { Img } from 'components/basic/Img'
import { VideoGif } from 'components/basic/VideoGif'
import { useMouseFaceTracking } from 'components/_hooks/useMouseFaceTracking'
import cx from 'classnames'
import { Type } from 'components/Type'

// Bigger number = less sensitive
export const TweetCard: FC<Omit<TweetItem, 'categories'> & { id: string; hideImage?: boolean }> = (props) => {
	const date = new Date(props.unixDateMS)
	const imgRef = useRef(null)
	const [containerRef, handleMouseMove, handleMouseLeave] = useMouseFaceTracking<any>()

	const content = (
		<>
			<div className={styles.tweet_logo_container}>{getLogo(props.platform)}</div>

			<div className={cx(styles.tweet_content_container, 'wall-of-love-tweet')}>
				<div className={styles.tweet_header}>
					<div className={styles.tweet_header_picture}>
						<img
							src={props.picture}
							alt={`${props.name} avatar`}
							ref={imgRef}
							onError={() => {
								if (imgRef) {
									imgRef.current.onError = null
									imgRef.current.src = 'https://eightsleep.imgix.net/twitter-avi-gender-balanced-figure.png?v=1687558204'
								}
							}}
						/>
					</div>
					<div className={styles.tweet_header_text}>
						<Type.SmallPrint>
							<strong>{props.name}</strong>
							{props.verified && props.platform !== 'threads' && (
								<span className={styles.verified_container}>
									<Verified />
								</span>
							)}
						</Type.SmallPrint>
						<Type.SmallPrint>@{props.handle}</Type.SmallPrint>
					</div>
				</div>
				<Type.Body2 className={styles.tweet_content}>
					<span dangerouslySetInnerHTML={{ __html: highlightEightSleep(props.content) }} />
				</Type.Body2>
				{props.image && !props.hideImage && (
					<div className={styles.tweet_image_container}>
						<Img
							src={props.image}
							alt={props.handle}
							dprHeight={500}
						/>
					</div>
				)}
				{props.video && (
					<div className={styles.tweet_image_container}>
						<VideoGif src={props.video} />
					</div>
				)}
				<div className={styles.tweet_footer}>
					<Type.SmallPrint>{formatDate(date)}</Type.SmallPrint>
				</div>
			</div>
		</>
	)

	if (props.url) {
		return (
			<a
				className={styles.container}
				href={props.url}
				id={props.id}
				key={props.url}
				ref={containerRef}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				target={'_blank'}
			>
				{content}
			</a>
		)
	}

	return (
		<div
			className={cx(styles.container)}
			ref={containerRef}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			id={props.id}
			key={props.id}
		>
			{content}
		</div>
	)
}

const highlightEightSleep = (text: string) => {
	return text.replace(/@eightsleep/gi, '<strong>@eightsleep</strong>')
}

const formatDate = (date: Date) => {
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

	const day = date.getDate()
	const monthIndex = date.getMonth()
	const year = date.getFullYear()

	return `${date.toLocaleTimeString([], {
		hour: '2-digit',
		minute: '2-digit',
	})} · ${monthNames[monthIndex]} ${day}, ${year}`
}

const getLogo = (platform: 'twitter' | 'threads') => {
	switch (platform) {
		case 'twitter':
			return <TwitterLogo />
		case 'threads':
			return <ThreadsLogo />
	}
}

const TwitterLogo = () => (
	<svg
		viewBox="0 0 1200 1227"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
			fill="black"
		/>
	</svg>
)

const ThreadsLogo = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 192 192"
	>
		<path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" />
	</svg>
)

const Verified = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="12"
		height="13"
		viewBox="0 0 12 13"
		fill="none"
	>
		<path
			d="M11.8328 6.78451C11.8328 7.17703 11.7387 7.54104 11.5506 7.87435C11.3625 8.20766 11.1109 8.46861 10.7937 8.65062C10.8025 8.70982 10.8069 8.80192 10.8069 8.92691C10.8069 9.52117 10.6078 10.0255 10.2141 10.4422C9.81812 10.861 9.34125 11.0693 8.78344 11.0693C8.53406 11.0693 8.29562 11.0233 8.07031 10.9312C7.89531 11.2908 7.64375 11.5802 7.31344 11.8017C6.98531 12.0254 6.62437 12.135 6.23281 12.135C5.8325 12.135 5.46937 12.0276 5.14562 11.8083C4.81969 11.5912 4.57031 11.2996 4.39531 10.9312C4.17 11.0233 3.93375 11.0693 3.68219 11.0693C3.12437 11.0693 2.64531 10.861 2.245 10.4422C1.84469 10.0255 1.64562 9.51898 1.64562 8.92691C1.64562 8.86113 1.65437 8.76903 1.66969 8.65062C1.3525 8.46642 1.10094 8.20766 0.912812 7.87435C0.726875 7.54104 0.632812 7.17703 0.632812 6.78451C0.632812 6.36787 0.737812 5.98413 0.945625 5.63766C1.15344 5.29119 1.43344 5.03463 1.78344 4.86798C1.69156 4.61799 1.64562 4.36582 1.64562 4.11583C1.64562 3.52377 1.84469 3.01722 2.245 2.60058C2.64531 2.18394 3.12437 1.97343 3.68219 1.97343C3.93156 1.97343 4.17 2.01948 4.39531 2.11158C4.57031 1.75196 4.82187 1.4625 5.15219 1.24103C5.48031 1.01955 5.84125 0.907715 6.23281 0.907715C6.62437 0.907715 6.98531 1.01955 7.31344 1.23883C7.64156 1.46031 7.89531 1.74976 8.07031 2.10939C8.29562 2.01729 8.53187 1.97124 8.78344 1.97124C9.34125 1.97124 9.81812 2.17956 10.2141 2.59839C10.61 3.01722 10.8069 3.52157 10.8069 4.11364C10.8069 4.38994 10.7653 4.63992 10.6822 4.86578C11.0322 5.03244 11.3122 5.289 11.52 5.63547C11.7278 5.98413 11.8328 6.36787 11.8328 6.78451ZM5.99437 8.47519L8.30656 5.00393C8.36562 4.91183 8.38312 4.81096 8.36344 4.70351C8.34156 4.59606 8.28687 4.51054 8.195 4.45353C8.10312 4.39432 8.0025 4.37459 7.89531 4.38994C7.78594 4.40748 7.69844 4.46011 7.63281 4.55221L5.59625 7.62218L4.65781 6.68364C4.57469 6.60032 4.47844 6.56084 4.37125 6.56523C4.26187 6.56962 4.16781 6.60909 4.08469 6.68364C4.01031 6.7582 3.97312 6.85249 3.97312 6.96652C3.97312 7.07835 4.01031 7.17265 4.08469 7.24939L5.37312 8.54097L5.43656 8.59141C5.51094 8.64184 5.5875 8.66597 5.66187 8.66597C5.80844 8.66377 5.92 8.60237 5.99437 8.47519Z"
			fill="#5289FE"
		/>
	</svg>
)
